import React from "react";
import {css, cva} from "@panda/css";

import {ResponsiveCardBody} from "./body_layouts/ResponsiveCardBody";
import {ResponsiveCardBodySimple} from "./body_layouts/ResponsiveCardBodySimple";
import {ResponsiveCardBodyWithIcon} from "./body_layouts/ResponsiveCardBodyWithIcon";
import {ResponsiveCardBodyWithImage} from "./body_layouts/ResponsiveCardBodyWithImage";
import {ResponsiveCardLayoutProps} from "./constants";
import {ResponsiveCardImgCover} from "./ResponsiveCardImgCover";

interface IProps extends ResponsiveCardLayoutProps {
    children: React.ReactNode;
}

type ResponsiveCardType = React.FC<IProps> & {
    Body: typeof ResponsiveCardBody;
    BodySimple: typeof ResponsiveCardBodySimple;
    BodyWithIcon: typeof ResponsiveCardBodyWithIcon;
    BodyWithImage: typeof ResponsiveCardBodyWithImage;
    ImgCover: typeof ResponsiveCardImgCover;
};

const ResponsiveCard: ResponsiveCardType = ({badges, cover, coverDesktopPosition = "left", withCoverPadding, coverWidthOnDesktop, children}) => {
    const hasBadges = Boolean(badges);

    return (
        <div className={cardBoxWrapperStyle({coverDesktopPosition, hasBadges})}>
            {badges && <div className={badgesWrapperStyle}>{badges}</div>}

            {cover && (
                <div
                    className={coverWrapperStyle({coverDesktopPosition, withPadding: withCoverPadding})}
                    style={{"--cover-width": coverWidthOnDesktop} as React.CSSProperties}
                >
                    {cover}
                </div>
            )}

            <div className={cardBoxBodyStyle}>{children}</div>
        </div>
    );
};

ResponsiveCard.Body = ResponsiveCardBody;
ResponsiveCard.BodySimple = ResponsiveCardBodySimple;
ResponsiveCard.BodyWithIcon = ResponsiveCardBodyWithIcon;
ResponsiveCard.BodyWithImage = ResponsiveCardBodyWithImage;
ResponsiveCard.ImgCover = ResponsiveCardImgCover;

export default ResponsiveCard;

const cardBoxWrapperStyle = cva({
    base: {
        display: "grid",
        overflow: "hidden",
        gridTemplateAreas: `"badge" "cover" "content"`,
        backgroundColor: "var(--colors-white)",
        borderRadius: 2,
        boxShadow: "var(--shadow-1)"
    },
    variants: {
        coverDesktopPosition: {
            left: {
                md: {
                    gridTemplateAreas: `"cover content"`,
                    gridTemplateColumns: "min-content auto"
                }
            },
            right: {
                md: {
                    gridTemplateAreas: `"content cover"`,
                    gridTemplateColumns: "auto min-content"
                }
            }
        },
        hasBadges: {
            true: {},
            false: {}
        }
    },
    compoundVariants: [
        {
            coverDesktopPosition: "left",
            hasBadges: true,
            css: {
                md: {
                    gridTemplateAreas: `"cover badge" "cover content"`,
                    gridTemplateColumns: "min-content auto"
                }
            }
        },
        {
            coverDesktopPosition: "right",
            hasBadges: true,
            css: {
                md: {
                    gridTemplateAreas: `"badge cover" "content cover"`,
                    gridTemplateColumns: "auto min-content"
                }
            }
        }
    ],
    defaultVariants: {
        coverDesktopPosition: "left",
        hasBadges: false
    }
});

const coverWrapperStyle = cva({
    base: {
        gridArea: "cover",
        lineHeight: 0,
        fontSize: 0,
        md: {
            width: "var(--cover-width, auto)"
        }
    },
    variants: {
        coverDesktopPosition: {
            left: {},
            right: {}
        },
        withPadding: {
            true: {
                pt: 2,
                pr: 2,
                pl: 2,
                md: {
                    pb: 2
                }
            },
            false: {}
        }
    },
    compoundVariants: [
        {
            coverDesktopPosition: "left",
            withPadding: true,
            css: {
                md: {
                    pr: 0
                }
            }
        },
        {
            coverDesktopPosition: "right",
            withPadding: true,
            css: {
                md: {
                    pl: 0
                }
            }
        }
    ],
    defaultVariants: {
        coverDesktopPosition: "left",
        withPadding: false
    }
});

const cardBoxBodyStyle = css({
    gridArea: "content",
    md: {
        order: 1
    }
});

const badgesWrapperStyle = css({
    paddingTop: 2,
    paddingRight: 2,
    paddingLeft: 2,
    paddingBottom: 0,
    gridArea: "badge",
    md: {
        display: "block",
        flex: "1 1 auto",
        order: 1,
        paddingTop: 2,
        paddingRight: 4,
        paddingLeft: 4,
        paddingBottom: 0
    }
});
